import { NodeViewSettingsPanel } from '@/components/common/node-view-settings-panel';
import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { createMatlabResource } from '@/components/tiptap-editor/tiptap-extension/zymathworks-learning-tool-extension/zymathworks-learning-tool-extension';
import { zyServer } from '@/lib/servers';
import { type ReactNodeViewProps } from '@tiptap/react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

type GenerateLaunchDataResponse = {
    success: boolean;
    launch_data?: Record<string, unknown>;
    launch_url?: string;
};

function getAuthoringZybookCode(): string | null {
    try {
        const currentUrl = new URL(window.location.href);
        const queryCode = currentUrl.searchParams.get('zybookCode')?.trim();
        if (queryCode) {
            return queryCode;
        }

        const pathSegments = currentUrl.pathname.split('/').filter(Boolean);
        const booksIndex = pathSegments.indexOf('books');
        if (booksIndex >= 0 && pathSegments.length > booksIndex + 1) {
            return decodeURIComponent(pathSegments[booksIndex + 1]);
        }
    } catch {
        return null;
    }

    return null;
}

const ZyMathWorksLearningToolNodeView: React.FC<ReactNodeViewProps> = ({ node, editor, updateAttributes, selected, decorations }) => {
    const { id, providerName, ltiResourceLinkId, caption } = node.attrs;
    const [isSigningLaunchData, setIsSigningLaunchData] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const signedForKeyRef = useRef<string | null>(null);

    const [runtimeLaunchUrl, setRuntimeLaunchUrl] = useState<string>('');
    const [runtimeLaunchData, setRuntimeLaunchData] = useState<Record<string, unknown>>({});
    const iframeName = useMemo(() => `mathworks-learning-tool-${String(id || 'new').replace(/[^a-zA-Z0-9_-]/g, '-')}`, [id]);
    const isEditorMode = editor.isEditable;
    const isLoadingAttrs = isEditorMode && Boolean(id) && runtimeLaunchUrl.length === 0 && Object.keys(runtimeLaunchData).length === 0;

    // Create MATLAB resource on mount if ltiResourceLinkId is not set
    useEffect(() => {
        if (!id || ltiResourceLinkId?.trim()) {
            return; // Already has resource or no id yet
        }

        let cancelled = false;

        const initializeResource = async () => {
            try {
                const resourceLinkId = await createMatlabResource();
                if (!cancelled) {
                    updateAttributes({ ltiResourceLinkId: resourceLinkId });
                }
            } catch (error) {
                if (!cancelled) {
                    setError('Unable to create MATLAB resource.');
                    console.warn('Failed to create MATLAB resource.', error);
                }
            }
        };

        void initializeResource();

        return () => {
            cancelled = true;
        };
    }, [id, ltiResourceLinkId, updateAttributes]);

    const [prevId, setPrevId] = useState(id);
    if (id !== prevId) {
        setPrevId(id);
    }

    useEffect(() => {
        if (!id) {
            return;
        }

        const normalizedResourceLinkId = String(ltiResourceLinkId || '').trim();
        if (!normalizedResourceLinkId) {
            return;
        }

        const idAsString = String(id);
        const signedKey = `${idAsString}:${normalizedResourceLinkId}`;
        if (signedForKeyRef.current === signedKey) {
            return;
        }

        let cancelled = false;

        const refreshEditableLaunchData = async () => {
            setIsSigningLaunchData(true);
            try {
                const response = await zyServer.post<GenerateLaunchDataResponse>('lti/generate_launch_data', {
                    body: {
                        provider_name: providerName || 'mathworks_learning_tool',
                        resource_link_id: normalizedResourceLinkId,
                        is_lab: true,
                        is_zml: false,
                        zybook_code: getAuthoringZybookCode(),
                        content_resource_id: id,
                    },
                });

                if (cancelled) {
                    return;
                }

                signedForKeyRef.current = signedKey;
                const launchUrl = response.launch_url || '';
                const launchData = response.launch_data || {};

                flushSync(() => {
                    setRuntimeLaunchUrl(launchUrl);
                    setRuntimeLaunchData(launchData);
                });

                if (launchUrl && Object.keys(launchData).length > 0) {
                    formRef.current?.submit();
                }
            } catch (signError) {
                if (!cancelled) {
                    setError('Unable to request editable MathWorks launch data.');
                    console.warn('Failed to refresh editable MathWorks launch data. Falling back to stored launch data.', signError);
                }
            } finally {
                if (!cancelled) {
                    setIsSigningLaunchData(false);
                }
            }
        };

        void refreshEditableLaunchData();

        return () => {
            cancelled = true;
        };
    }, [id, providerName, ltiResourceLinkId, isEditorMode]);

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label="MathWorks learning tool"
            selected={selected}
            settingsPanel={
                <NodeViewSettingsPanel
                    fields={[
                        {
                            label: 'Caption',
                            type: 'text',
                            value: caption ?? '',
                            onChange: event => updateAttributes({ caption: event.target.value }),
                            placeholder: 'Caption...',
                            searchAttr: 'caption',
                        },
                    ]}
                />
            }
            contentClassName="px-4 py-4"
        >
            <div
                contentEditable={false}
                className="w-full overflow-hidden rounded border bg-white mx-auto"
                style={{ minHeight: '420px', height: '700px' }}
            >
                <iframe
                    name={iframeName}
                    src="about:blank"
                    title={caption || 'MathWorks learning tool'}
                    className="h-full w-full border-0"
                />
            </div>

            {isLoadingAttrs ? <div className="mt-2 text-xs text-gray-500">Loading MathWorks launch data...</div> : null}
            {isSigningLaunchData ? <div className="mt-2 text-xs text-gray-500">Preparing editable MathWorks session...</div> : null}
            {error ? <div className="mt-2 text-xs text-red-600">{error}</div> : null}

            <form
                ref={formRef}
                action={runtimeLaunchUrl}
                method="POST"
                target={iframeName}
                className="hidden"
            >
                {Object.entries(runtimeLaunchData).map(([key, value]) => (
                    <input
                        key={key}
                        name={key}
                        type="hidden"
                        value={typeof value === 'string' ? value : JSON.stringify(value)}
                    />
                ))}
            </form>
        </CommonNodeViewWrapper>
    );
};

export default memo(ZyMathWorksLearningToolNodeView);
